import React from "react";
import { useTheme, withStyles } from "@material-ui/core";
import { ExpandLess, ExpandMore } from "@material-ui/icons";
import { DataGridPro, GridColDef, GridToolbarColumnsButton, GridToolbarContainer, GridToolbarExport, GridToolbarFilterButton } from "@mui/x-data-grid-pro";
import Spacer from "components/Spacer";

import { useAppSelector } from "store";
import { selectSelectedStoreByCostType } from "modules/customer/insights/cost/costSlice";
import { DataType } from "modules/customer/insights/cost/storeCostActions/storeCostActions";
import { selectFilteredCostActions } from "modules/customer/insights/cost/storeCostActions/storeCostActionsSlice";
import { DateTime, Duration } from "luxon";

const StyledDataGrid = withStyles(theme => ({
    root: {
        border: 'none',
        backgroundColor: theme.palette.background.paper,
        paddingTop: theme.spacing(2),
        paddingLeft: theme.spacing(2),
        '& .MuiCheckbox-root': {
            color: theme.palette.text.primary,
            '&.Mui-checked': {
                //@ts-ignore
                color: theme.palette.tertiary.main,
            },
        },
        '& .MuiDataGrid-row.Mui-selected': {
            backgroundColor: 'rgba(255, 255, 255, 0.08)',
            '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.16)',
            },
        },
        '& .MuiDataGrid-cell:focus': {
            outline: 'none'
        },
        '& .MuiDataGrid-cell:focus-within': {
            outline: 'none'
        }
    }
}))(DataGridPro);

const CostActionsDataGrid: React.FC = () => {
    const theme = useTheme();
    const store = useAppSelector(selectSelectedStoreByCostType);
    const { data: rows, isLoading } = useAppSelector(selectFilteredCostActions);

    const columns: GridColDef[] = [
        {
            field: 'action',
            flex: 1,
            headerName: 'Action',
            sortable: true,
        },
        {
            field: 'storeValue',
            flex: 1,
            headerName: `${store?.storeName} store's value`,
            sortable: false,
            align: "right",
            headerAlign: "right",
            valueGetter: (params) => {
                if (params.row.dataType === DataType.Date) {
                    if (!params.value) {
                        return null;
                    }
                    return (params.value as DateTime).toFormat("dd/LL/yyyy");
                }
                else if (params.row.dataType === DataType.Time) {
                    if (!params.value) {
                        return null;
                    }
                    return (params.value as DateTime).toFormat("HH:mm");
                }
                else {
                    return params.value;
                }
            }
        },
        {
            field: 'clusterAverageValue',
            flex: 1,
            headerName: 'Cluster average value',
            sortable: false,
            align: "right",
            headerAlign: "right",
            valueGetter: (params) => {
                if (params.row.dataType === DataType.Date) {
                    if (!params.value) {
                        return null;
                    }
                    return (params.value as DateTime).toFormat("dd/LL/yyyy");
                }
                else if (params.row.dataType === DataType.Time) {
                    if (!params.value) {
                        return null;
                    }
                    return (params.value as DateTime).toFormat("HH:mm");
                }
                else {
                    return params.value;
                }
            }
        },
        {
            field: 'targetValue',
            flex: 1,
            headerName: 'Target value',
            sortable: false,
            align: "right",
            headerAlign: "right",
            valueGetter: (params) => {
                if (params.row.dataType === DataType.Date) {
                    if (!params.value) {
                        return null;
                    }
                    return (params.value as DateTime).toFormat("dd/LL/yyyy");
                }
                else if (params.row.dataType === DataType.Time) {
                    if (!params.value) {
                        return null;
                    }
                    return (params.value as DateTime).toFormat("HH:mm");
                }
                else {
                    return params.value;
                }
            }
        },
        {
            field: 'targetVariance',
            flex: 1,
            headerName: `Target variance`,
            sortable: false,
            align: "right",
            headerAlign: "right",
            valueGetter: (params) => {
                if (params.row.dataType === DataType.Date) {
                    if (!params.value) {
                        return null;
                    }
                    if (params.value === 1 || params.value === -1) {
                        return `${params.value} day`;
                    }

                    return `${params.value} days`;
                }
                else if (params.row.dataType === DataType.Time) {
                    let dur = (params.value as Duration).toObject();
                    if (!dur.hours || !dur.minutes) {
                        return null;
                    }
                    return `${Math.abs(dur.hours).toString().padStart(2, "0")}:${Math.abs(dur.minutes).toString().padStart(2, "0")}`;
                }
                else {
                    return params.value;
                }
            }
        },
    ];

    const columnsWithWidth = columns.map(column => {
        return {
            ...column,
            minWidth: 200
        };
    });

    function CustomToolbar() {
        return (
            <GridToolbarContainer style={{ paddingRight: theme.spacing(2) }}>
                <GridToolbarColumnsButton style={{ color: theme.palette.text.primary }} />
                <GridToolbarFilterButton style={{ color: theme.palette.text.primary }} />
                <Spacer />
                {/* @ts-ignore */}
                <GridToolbarExport style={{ color: theme.palette.tertiary.main }} />
            </GridToolbarContainer>
        );
    }

    return (
        <StyledDataGrid
            rows={rows}
            columns={columnsWithWidth}
            checkboxSelection
            disableSelectionOnClick
            loading={isLoading}
            components={{
                Toolbar: CustomToolbar,
                ColumnSortedAscendingIcon: ExpandLess,
                ColumnSortedDescendingIcon: ExpandMore
            }}
            sortingOrder={['asc', 'desc']}
            hideFooter
        />
    );
};

export default CostActionsDataGrid;
